export interface GroupPortraitPerson {
  uuid: string;
  bbox?: readonly number[] | null;
  gender?: string | null;
  age_group?: string | null;
  race?: string | null;
}

interface PhotoWithFaces {
  faces?: readonly { uuid: string }[] | null;
}

type AgeBand = 'child' | 'teen' | 'adult' | 'senior';

// The coarse bands of the backend AGE_BANDS adapter. An age group outside
// this table (an old detector value, for example) gets no band.
const AGE_BANDS: Readonly<Record<string, AgeBand>> = {
  '0-2': 'child',
  '3-9': 'child',
  '0-9': 'child',
  '10-19': 'teen',
  '20-29': 'adult',
  '30-39': 'adult',
  '40-49': 'adult',
  '50-59': 'adult',
  '60-69': 'senior',
  '70+': 'senior',
};
const BAND_ORDER: readonly string[] = ['child', 'teen', 'adult', 'senior'];
const GENDER_ORDER: readonly string[] = ['female', 'male', 'non-binary'];

function centerX(person: GroupPortraitPerson): number {
  const box = person.bbox;
  if (!(box && box.length >= 4)) {
    return Number.POSITIVE_INFINITY;
  }
  return ((box[0] ?? 0) + (box[2] ?? 0)) / 2;
}

/**
 * Put the people of a group in one order, left to right: by photo, then by
 * the x of the center of the face box. A face with no photo or no box goes
 * last, in its given position.
 *
 * The backend gives person k the template slot "Image k", and the image
 * model mixes up two faces when that order and the photo order disagree.
 * The photo uuids and the subjects of a submit thus use this one order.
 *
 * Complexity: O(p + n log n) for p faces on the photos and n people.
 */
export function orderPeopleLeftToRight<T extends GroupPortraitPerson>(
  people: readonly T[],
  photos: readonly PhotoWithFaces[]
): T[] {
  const photoIndexOf = new Map<string, number>();
  photos.forEach((photo, index) => {
    for (const face of photo.faces ?? []) {
      photoIndexOf.set(face.uuid, index);
    }
  });
  const photoIndex = (person: T): number =>
    photoIndexOf.get(person.uuid) ?? Number.POSITIVE_INFINITY;
  const compare = (left: number, right: number): number => {
    if (left === right) {
      return 0;
    }
    return left < right ? -1 : 1;
  };
  return [...people].sort(
    (a, b) =>
      compare(photoIndex(a), photoIndex(b)) || compare(centerX(a), centerX(b))
  );
}

function rank(order: readonly string[], value: string | undefined): number {
  const index = value === undefined ? -1 : order.indexOf(value);
  return index === -1 ? order.length : index;
}

/**
 * The singles of each face when a session makes `total` singles for
 * `faceCount` faces. The first faces take the singles that do not divide
 * equally: 5 for 2 faces is 3 + 2, and 5 for 3 faces is 2 + 2 + 1.
 */
export function splitSinglesOverFaces(
  total: number,
  faceCount: number
): number[] {
  const singles = Math.max(0, total);
  const base = faceCount > 0 ? Math.floor(singles / faceCount) : 0;
  const remainder = faceCount > 0 ? singles % faceCount : 0;
  return Array.from({ length: Math.max(0, faceCount) }, (_, index) =>
    index < remainder ? base + 1 : base
  );
}

/**
 * The kinds of the people of a group, sorted: by age band (child, teen,
 * adult, senior), then by gender (female, male, non-binary). The backend
 * matches a group to a story with the same key. Race does not change the
 * match, thus it is not in the key.
 */
export function groupKey(people: readonly GroupPortraitPerson[]): string {
  return people
    .map((person) => ({
      band: AGE_BANDS[person.age_group ?? ''],
      gender: person.gender ?? undefined,
    }))
    .sort(
      (a, b) =>
        rank(BAND_ORDER, a.band) - rank(BAND_ORDER, b.band) ||
        rank(GENDER_ORDER, a.gender) - rank(GENDER_ORDER, b.gender)
    )
    .map(({ band, gender }) => `${band ?? 'unknown'}:${gender ?? 'unknown'}`)
    .join(',');
}
